import React from 'react'
import { COMPONENT_APP_SCHEMA, ImportAppJSON, ImportAppJsonConfig } from './ImportAppJSON'
import { Label } from '../../ui-primitives/label'
import { produce } from 'immer'
import { WistorVarName, WistorVarValue } from '../../../redux/pubsub'
import { TextTableInput } from '../../ui-primitives/small/text-table-input'
import { stableRefTo } from '../../../utils/react-utils'
import { uniqueId } from 'lodash'

export type ComponentConfig
  = {}
  | {
      appRef: ImportAppJsonConfig
      namespace: string
      varMapping: ComponentVarMapping
    }

type ComponentVarMapping = { input: VarMap[], output: VarMap[] }
export type VarMap = [sourceName: WistorVarName, targetName: WistorVarName]
export type VarMapRecord = Record<VarMap[0], Record<VarMap[1], WistorVarValue>>

type State =
  | { phase: 'nothing-configured' }
  | { phase: 'no-app-json-configured', appRef: ImportAppJsonConfig }
  | { phase: 'invalid-app-json-configured', appRef: ImportAppJsonConfig, errorMessage: string }
  | { phase: 'maybe-complete', appRef: ImportAppJsonConfig, namespace: string, varMapping: ComponentVarMapping }

export function SetComponentConfig(props: {
  value: ComponentConfig | null | undefined
  onChange: (value: ComponentConfig) => void | Promise<void>
}): React.ReactNode {

  const [state, setState] = React.useState<State>(() => {
    return props.value && 'appRef' in props.value
      ? {phase: 'maybe-complete', ...props.value}
      : {phase: 'nothing-configured'}
  })

  const {onChange} = props
  React.useEffect(() => {
    const shouldSave = state !== props.value && 'varMapping' in state

    if (shouldSave) {
      onChange(state)
    }
  }, [props.value, state, onChange])

  return (
    <div>
      <ImportAppJSON
        name='not needed?'
        value={'appRef' in state ? state.appRef : null}
        onChange={appRef => setState((prevState): State => {
          if (!appRef?.appConfig) return {phase: 'no-app-json-configured', appRef}

          const parsed = COMPONENT_APP_SCHEMA.safeParse(appRef.appConfig)
          if (!parsed.success) {
            console.error(`could not parse ${JSON.stringify(appRef.appConfig)}`, parsed.error)
            return {
              phase: 'invalid-app-json-configured',
              appRef, errorMessage: 'invalid component configuration'
            }
          }

          const prevVarMapping = 'varMapping' in prevState ? prevState.varMapping : null

          return {
            phase: 'maybe-complete',
            appRef,
            namespace: uniqueId(parsed.data.title + ' -- '),
            varMapping: {
              input: (parsed.data.componentInputVarNames ?? []).map(inputName => [
                prevVarMapping?.input?.find(([, n]) => n === inputName)?.[0] ?? '',
                inputName,
              ]),
              output: (parsed.data.componentOutputVarNames ?? []).map(outputName => [
                outputName,
                prevVarMapping?.output?.find(([n]) => n === outputName)?.[1] ?? '',
              ]),
            },
          }
        })}
      />
      
      {state.phase === 'maybe-complete' && (
        <div>

          {state.varMapping.input && (
            <div>
              <Label>Input mapping</Label>
              <TextTableInput staticRows
                columnLabels={['app', 'input']}
                disabledColumnIndexes={stableRefTo([1], "disabled-indexes-component-input-mapping-table")}
                value={state.varMapping.input}
                onChange={rows => setState(produce(draft => {
                  if (!('varMapping' in draft && draft.varMapping)) throw new Error('Oops! a developer made a mistake')
                  draft.varMapping.input = rows as VarMap[]
                }))}
              />
            </div>
          )}

          {state.varMapping.output && (
            <div>
              <Label>Output mapping</Label>
              <TextTableInput staticRows
                columnLabels={['output', 'app']}
                disabledColumnIndexes={stableRefTo([0], "disabled-indexes-component-output-mapping-table")}
                value={state.varMapping.output}
                onChange={rows => setState(produce(draft => {
                  if (!('varMapping' in draft && draft.varMapping)) throw new Error('Oops! a developer made a mistake')
                  draft.varMapping.output = rows as VarMap[]
                }))}
              />
            </div>
          )}

        </div>
      )}
    </div>
  )

}
